// @ts-nocheck
import { useState, useEffect } from "react"
import { Slice } from "gatsby"
import { Stack } from "@mui/material"
import { useDispatch, addNode, removeNode, useSelector } from "@ggs/store"
import { get, isEmpty, find } from "lodash"
import Layout from "../components/layout"

// geo page components
import {
  Hero,
  ContentHighlight,
  ContactCTA,
  VisualMediaContent,
  Facts,
  DynamicSubNav,
  SectionSwitcher,
  GlobalAdBanners,
  SuggestedTrips,
  Teaser,
} from "../components"

import {
  useGlobalAdsSolrData,
  useStaffData,
  useArticlesSolrData,
  useDynamicSubNav,
  useTripsData,
} from "../local/hooks"
import { mapSectionSwitcher, mapWishlistData, mapArticleTeaser } from "../helpers/data-mappers"

export default function GeoPageView(props) {
  const data = get(props, "data", {})
  const dispatch = useDispatch()
  const wishlist = useSelector((state) => state.views.wishlist)
  const likedArticles = wishlist?.article && wishlist?.article.map((article) => article.nid)
  const likedTrips = wishlist?.trip && wishlist?.trip.map((trip) => trip.nid)
  const { getCountryGlobalAds, getDestinationGlobalAds } = useGlobalAdsSolrData()
  const { getStaffByDestination } = useStaffData()
  const { getTripsByLocation } = useTripsData()
  const { getArticles } = useArticlesSolrData()
  const [globalAds, setGlobalAds] = useState(null)
  const [contactCtaStaff, setContactCtaStaff] = useState(null)
  const [articles, setArticles] = useState(null)
  const [suggestedTrips, setSuggestedTrips] = useState(null)
  const [tripItineraryUrl, setTripItineraryUrl] = useState(null)
  const userCountry = useSelector((state) => state.settings.country)
  const refCountryId = data.parentCountryId || ""
  const refDestinationId = data.country?.[0]?.destinations?.[0]?.external_id || ""

  const resolveGlobalAds = async () => {
    const countryAds = await getCountryGlobalAds(refCountryId)
    setGlobalAds(countryAds)
    // When no ads available for country, get ads for destination.
    if (isEmpty(countryAds)) {
      const destinationAds = await getDestinationGlobalAds(refDestinationId)
      setGlobalAds(destinationAds)
    }
  }

  const country = get(data, "country[0]", null)
  const subNav = useDynamicSubNav({
    title: country?.title || "Overview",
    uri: country?.link?.uri || "#",
    nid: country?.external_id,
  })

  useEffect(() => {
    const tripUrl = find(subNav, (navLink) => navLink.title === "Trip Itineraries")?.uri
    const city = data?.title || ""
    const locationId = data?.external_id || ""
    const encodedLocation = encodeURIComponent(`${city}:${locationId}`)
    const filteredTripUrl = `${tripUrl}?location=${encodedLocation}&page=1`
    setTripItineraryUrl(filteredTripUrl)
  }, [subNav, data])

  const getBreadcrumbs = () => {
    const breadcrumbs = [
      {
        title: "Home",
        uri: "/",
      },
      {
        title: "Destinations",
        uri: "/destinations",
      },
    ]

    if (data?.country?.[0]?.destinations?.[0]) {
      breadcrumbs.push({
        title: data?.country?.[0]?.destinations?.[0]?.title,
        uri: data?.country?.[0]?.destinations?.[0]?.slug,
      })
    }

    if (data?.country?.[0]) {
      breadcrumbs.push({
        title: data?.country?.[0]?.title,
        uri: data?.country?.[0]?.link?.uri,
      })
    }

    breadcrumbs.push({
      title: data?.title,
    })

    return breadcrumbs
  }

  const {
    hero,
    destinationId,
    content_highlight,
    contact_cta,
    vmedia_content,
    facts_overview,
    teaser_3up,
    suggested_trips,
    section_switcher,
  } = data

  // Resolve Solr data.
  useEffect(() => {
    resolveGlobalAds().then(() => console.log("Resolving global ads"))
    getStaffByDestination(refDestinationId).then((data) => {
      setContactCtaStaff(data)
    })
    getArticles(teaser_3up, refCountryId, refDestinationId).then((data) => {
      const filteredData = mapWishlistData(likedArticles, data)
      setArticles(filteredData)
    })
  }, [])

  useEffect(() => {
    const fetchAndSetTrips = async () => {
      // Get trips data
      const tripsData = await getTripsByLocation(data.external_id)
      const filteredData = mapWishlistData(likedTrips, tripsData)

      // Set suggested trips data
      setSuggestedTrips({
        title: suggested_trips?.title || "",
        subtitle: suggested_trips?.subtitle || "",
        link: {
          title: "All Trips",
          uri: tripItineraryUrl || "/trips",
        },
        items: filteredData,
      })
    }

    fetchAndSetTrips()
  }, [userCountry, tripItineraryUrl])

  const addToWishList = (selectedEntity) => {
    if (selectedEntity.contentType === "article") {
      const filteredState = mapArticleTeaser(articles, selectedEntity, true)
      setArticles(filteredState)
    }
    if (selectedEntity.contentType === "trip") {
      const filteredState = mapArticleTeaser(suggestedTrips.items, selectedEntity, true)
      setSuggestedTrips({
        ...suggestedTrips,
        items: filteredState,
      })
    }
    dispatch(
      addNode({ type: selectedEntity.contentType, entity: selectedEntity, idPropertyKey: "nid" })
    )
  }

  const removeFromWishList = (selectedEntity) => {
    if (selectedEntity.contentType === "article") {
      const filteredState = mapArticleTeaser(articles, selectedEntity, false)
      setArticles(filteredState)
    }
    if (selectedEntity.contentType === "trip") {
      const filteredState = mapArticleTeaser(suggestedTrips.items, selectedEntity, false)
      setSuggestedTrips({
        ...suggestedTrips,
        items: filteredState,
      })
    }
    dispatch(removeNode({ type: selectedEntity.contentType, findPredicate: selectedEntity }))
  }

  return (
    <Layout
      hero={
        <Hero
          title={hero?.title}
          subtitle={hero?.subtitle}
          media={hero?.images}
          location={hero?.location}
          subtitleImageUrl={hero?.subtitleImageUrl}
          video={hero?.video}
          path={getBreadcrumbs()}
        />
      }
    >
      {subNav && <DynamicSubNav links={subNav} />}
      <Facts
        title={facts_overview?.title}
        subtitle={facts_overview?.subtitle}
        description={facts_overview?.description}
        items={facts_overview?.items}
      />
      {suggestedTrips?.items?.length && (
        <SuggestedTrips {...suggestedTrips} onLike={addToWishList} onUnlike={removeFromWishList} />
      )}
      <ContentHighlight
        color={content_highlight?.background_color}
        title={content_highlight?.title}
        subtitle={content_highlight?.subtitle}
        description={content_highlight?.description}
        image={content_highlight?.image}
        link={content_highlight?.link}
      />
      {/* @ts-ignore */}
      {section_switcher?.items && (
        <SectionSwitcher items={mapSectionSwitcher(section_switcher?.items)} />
      )}
      <VisualMediaContent images={vmedia_content?.images} />
      {globalAds && <GlobalAdBanners items={globalAds} />}
      <Teaser
        title={teaser_3up?.title}
        subtitle={teaser_3up?.subtitle}
        link={teaser_3up?.link}
        items={articles}
        onLike={addToWishList}
        onUnlike={removeFromWishList}
      />
      <ContactCTA
        title={contact_cta?.title}
        subtitle={contact_cta?.subtitle}
        description={contact_cta?.description}
        link={contact_cta?.link}
        image={contact_cta?.image}
        staff={contactCtaStaff}
      />
      <Slice alias="preFooterStyle1" destinationId={destinationId} />
    </Layout>
  )
}
